/**
 * The hosted simulation's stepping half, shared by the main thread
 * (NetworkedSimRunner) and the host's Web Worker (sim.worker.ts), which
 * phop requires to behave the same. Both build the sim context with
 * `matchContext` from the match alone (the island from its seed and radius,
 * the base rules): the worker has no React props, so a context the main
 * thread got any other way could silently differ from the worker's.
 */
import type { PlayerInput as PhopInput } from '@peterddod/phop/worker';
import { generateIslandMap } from '../hexmap/generation/island';
import type { RuleSet } from '../rules/schema';
import { tick } from '../sim/tick';
import type { Command, MatchConfig, SimContext, SimState } from '../sim/types';

/** Advance `state` one fixed step with phop's inputs (sender id from the transport). */
function stepSim(
  state: SimState,
  ctx: SimContext,
  inputs: readonly PhopInput<Command>[],
  dt: number
): void {
  tick(
    state,
    ctx,
    inputs.map(({ playerId, input }) => ({ playerId, command: input })),
    dt
  );
}

/** `tick()` no-ops once the match is over; phop stops stepping and broadcasting. */
function simRunning(state: SimState): boolean {
  return state.status === 'running';
}

/** The sim context for `match`: its island (from seed and radius) and `rules`. */
function matchContext(rules: RuleSet, match: MatchConfig): SimContext {
  return { terrain: generateIslandMap(match.mapRadius, match.seed), rules };
}

/**
 * A context factory for a step that only sees the state: `matchContext` for
 * `state.match`, cached per match seed and radius.
 */
function contextFromState(rules: RuleSet): (state: SimState) => SimContext {
  let cached: { key: string; ctx: SimContext } | null = null;
  return (state) => {
    const match = state.match;
    if (!match) throw new Error('sim state has no match: cannot rebuild the island');
    const key = `${match.mapRadius}:${match.seed}`;
    if (cached?.key !== key) {
      cached = { key, ctx: matchContext(rules, match) };
    }
    return cached.ctx;
  };
}

export { contextFromState, matchContext, simRunning, stepSim };
